import { unref } from 'vue'
import type { MaybeRef } from 'vue'
import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { AuthArgs } from '~/api/models/index'
import { activeQueryLicenceScope, type QueryLicenceScope } from '~/api/models/report-service'
import type {
  DashboardDuplicateInput,
  DashboardInput,
  DashboardTemplateDuplicateInput,
  DashboardsQuery,
  DefaultDashboardInput,
  LayoutItem,
  WidgetInput
} from '~/types/dashboard'

export const DASHBOARDS_QUERY_KEY = 'dashboards'
export const DASHBOARD_QUERY_KEY = 'dashboard'
export const DASHBOARD_DEFAULT_QUERY_KEY = 'dashboardDefault'

export interface DashboardListArgs extends DashboardsQuery {
  locale: string
}

export interface DashboardByIdArgs {
  id: string
  locale: string
}

export const dashboardsQueryKey = (scope: QueryLicenceScope, args: DashboardListArgs): unknown[] => [
  DASHBOARDS_QUERY_KEY,
  { include: args.include ?? null, locale: args.locale },
  scope
]

export const dashboardQueryKey = (scope: QueryLicenceScope, args: DashboardByIdArgs): unknown[] => [
  DASHBOARD_QUERY_KEY,
  args.id,
  args.locale,
  scope
]

export const dashboardDefaultQueryKey = (scope: QueryLicenceScope): unknown[] => [DASHBOARD_DEFAULT_QUERY_KEY, scope]

const queries = {
  getAll: {
    queryFn: (args: MaybeRef<DashboardListArgs>) => {
      const { include, locale } = unref(args)
      return useApi().reportService.listDashboards(include ? { include } : {}, locale)
    },
    queryKey: (args: MaybeRef<DashboardListArgs>) => dashboardsQueryKey(activeQueryLicenceScope(), unref(args)),
    options: {
      staleTime: () => 1000 * 30
    }
  },
  getById: {
    queryFn: (args: MaybeRef<DashboardByIdArgs>) => {
      const { id, locale } = unref(args)
      return useApi().reportService.getDashboard(id, locale)
    },
    queryKey: (args: MaybeRef<DashboardByIdArgs>) => dashboardQueryKey(activeQueryLicenceScope(), unref(args)),
    options: {
      staleTime: () => 1000 * 30,
      enabled: (args: MaybeRef<DashboardByIdArgs>) => !!unref(args).id
    }
  },
  getDefault: {
    queryFn: () => useApi().reportService.getDefaultDashboard(),
    queryKey: () => dashboardDefaultQueryKey(activeQueryLicenceScope()),
    options: {
      staleTime: () => 1000 * 60,
      retry: () => 0
    }
  }
} as const

const mutations = {
  create: {
    request: (input: DashboardInput) => useApi().reportService.createDashboard(input),
    cacheKey: () => [[DASHBOARDS_QUERY_KEY]]
  },
  update: {
    request: (args: { id: string; input: DashboardInput }) =>
      useApi().reportService.updateDashboard(args.id, args.input),
    cacheKey: (args: { id: string; input: DashboardInput }) => [[DASHBOARDS_QUERY_KEY], [DASHBOARD_QUERY_KEY, args.id]]
  },
  delete: {
    request: (id: string) => useApi().reportService.deleteDashboard(id),
    cacheKey: (id: string) => [[DASHBOARDS_QUERY_KEY], [DASHBOARD_QUERY_KEY, id], [DASHBOARD_DEFAULT_QUERY_KEY]]
  },
  updateLayout: {
    request: (args: { id: string; layout: LayoutItem[] }) =>
      useApi().reportService.updateDashboardLayout(args.id, args.layout),
    cacheKey: (args: { id: string; layout: LayoutItem[] }) => [[DASHBOARD_QUERY_KEY, args.id]]
  },
  createWidget: {
    request: (args: { dashboardId: string; input: WidgetInput }) =>
      useApi().reportService.createWidget(args.dashboardId, args.input),
    cacheKey: (args: { dashboardId: string; input: WidgetInput }) => [
      [DASHBOARDS_QUERY_KEY],
      [DASHBOARD_QUERY_KEY, args.dashboardId]
    ]
  },
  updateWidget: {
    request: (args: { dashboardId: string; widgetId: string; input: WidgetInput }) =>
      useApi().reportService.updateWidget(args.dashboardId, args.widgetId, args.input),
    cacheKey: (args: { dashboardId: string; widgetId: string; input: WidgetInput }) => [
      [DASHBOARD_QUERY_KEY, args.dashboardId]
    ]
  },
  deleteWidget: {
    request: (args: { dashboardId: string; widgetId: string }) =>
      useApi().reportService.deleteWidget(args.dashboardId, args.widgetId),
    cacheKey: (args: { dashboardId: string; widgetId: string }) => [
      [DASHBOARDS_QUERY_KEY],
      [DASHBOARD_QUERY_KEY, args.dashboardId]
    ]
  },
  duplicate: {
    request: (args: { id: string; input?: DashboardDuplicateInput; locale: string }) =>
      useApi().reportService.duplicateDashboard(args.id, args.input ?? {}, args.locale),
    cacheKey: () => [[DASHBOARDS_QUERY_KEY]]
  },
  duplicateFromTemplate: {
    request: (args: { templateKey: string; input?: DashboardTemplateDuplicateInput; locale: string }) =>
      useApi().reportService.duplicateDashboardFromTemplate(args.templateKey, args.input ?? {}, args.locale),
    cacheKey: () => [[DASHBOARDS_QUERY_KEY]]
  },
  setDefault: {
    request: (input: DefaultDashboardInput) => useApi().reportService.setDefaultDashboard(input),
    cacheKey: () => [[DASHBOARD_DEFAULT_QUERY_KEY]]
  },
  setLicenceDefault: {
    request: (input: DefaultDashboardInput) => useApi().reportService.setLicenceDefaultDashboard(input),
    cacheKey: () => [[DASHBOARD_DEFAULT_QUERY_KEY]]
  }
} as const

export type DashboardsModelService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): DashboardsModelService =>
  defineService(authArgs, { queries, mutations })
